import React, { useEffect, useRef } from 'react';
import ScrollReveal from './ScrollReveal';
import { trackDashboardLinkClick } from '../utils/analytics';

/* ── KPI card data ─────────────────────────────────────── */
const kpis = [
  { label: 'Patient Activity This Week', value: '219', delta: '+29.9%', trend: 'up' },
  { label: 'Hospital Occupancy', value: '74', unit: 'patients', delta: '+86.0%', trend: 'up' },
  { label: 'Occupancy 95%ile', value: '162', unit: 'patients' },
  { label: 'Ambulance Availability %', value: '37.7%', warn: true },
  { label: 'Avg Visit Duration', value: '394', unit: 'min', delta: '+34.0%', warn: true },
];

/* ── Heatmap data (day × hour) ─────────────────────────── */
const heatmapRows = [
  { day: 'Sat', cells: [12,18,25,30,42,55,68,74,88,95,102,110,118,125,132,128,115,98,80,62,45,30,20,14] },
  { day: 'Fri', cells: [8,14,22,35,58,82,105,120,135,142,148,155,160,158,152,140,125,108,85,60,42,28,16,10] },
  { day: 'Thu', cells: [10,16,28,45,72,98,128,145,155,162,167,165,162,158,155,148,138,122,95,68,48,32,18,12] },
  { day: 'Wed', cells: [6,10,18,28,48,72,95,108,118,125,130,128,125,120,115,108,95,80,62,45,32,22,14,8] },
  { day: 'Tue', cells: [10,15,25,42,68,95,125,140,150,158,162,160,155,150,145,138,128,110,88,62,44,30,18,12] },
  { day: 'Mon', cells: [5,8,14,22,38,58,78,90,98,105,110,108,105,100,95,88,78,65,48,35,24,16,10,6] },
  { day: 'Sun', cells: [4,6,10,14,22,32,42,50,55,58,60,58,55,52,48,42,35,28,22,16,12,8,6,4] },
];
const heatmapMax = 167;

/* ── Occupancy area chart data (24 hrs) ────────────────── */
const occupancyData = [
  8,12,10,8,6,10,18,35,62,88,105,120,130,125,118,108,115,128,142,155,130,95,55,25
];
const occupancyBandLow = [
  4,6,5,4,3,5,10,20,40,60,75,88,95,90,85,78,82,92,105,115,95,65,35,14
];
const occupancyBandHigh = [
  14,20,18,14,10,18,30,55,88,120,140,155,165,160,152,142,150,165,178,190,168,130,80,40
];

/* ── Visit duration area chart data (24 hrs) ──────────── */
const visitData = [
  20,15,12,10,8,12,25,80,180,320,420,580,650,700,520,380,280,350,480,620,750,480,180,60
];
const visitBandLow = [
  10,8,6,5,4,6,12,40,90,160,220,300,340,360,260,190,140,180,240,320,380,240,90,30
];
const visitBandHigh = [
  40,30,24,20,16,24,50,160,360,640,840,1100,1300,1400,1040,760,560,700,960,1240,1500,960,360,120
];

/* ── Movement pattern data ─────────────────────────────── */
function generatePaths(seed) {
  const paths = [];
  const rng = (s) => {
    s = ((s + 0x6D2B79F5) | 0);
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  let s = seed;
  for (let i = 0; i < 35; i++) {
    s += 1;
    const x1 = rng(s) * 100; s += 1;
    const y1 = 15 + rng(s) * 70; s += 1;
    const x2 = rng(s) * 100; s += 1;
    const y2 = 15 + rng(s) * 70; s += 1;
    const cx = (x1 + x2) / 2 + (rng(s) - 0.5) * 30; s += 1;
    const cy = (y1 + y2) / 2 + (rng(s) - 0.5) * 30;
    const type = rng(s + 99) > 0.6 ? 'person' : rng(s + 99) > 0.3 ? 'vehicle' : 'other';
    paths.push({ x1, y1, x2, y2, cx, cy, type });
  }
  return paths;
}
const movementPaths = generatePaths(42);

/* ── Helpers ───────────────────────────────────────────── */
function heatColor(val) {
  const t = val / heatmapMax;
  const r = Math.round(8 + t * 10);
  const g = Math.round(40 + t * 140);
  const b = Math.round(80 + t * 175);
  return `rgb(${r},${g},${b})`;
}


function buildAreaPath(data, maxVal, w, h, padTop) {
  const step = w / (data.length - 1);
  let d = `M0,${h}`;
  data.forEach((v, i) => {
    d += ` L${i * step},${padTop + (1 - v / maxVal) * (h - padTop)}`;
  });
  d += ` L${w},${h} Z`;
  return d;
}

function buildLinePath(data, maxVal, w, h, padTop) {
  const step = w / (data.length - 1);
  let d = '';
  data.forEach((v, i) => {
    const x = i * step;
    const y = padTop + (1 - v / maxVal) * (h - padTop);
    d += i === 0 ? `M${x},${y}` : ` L${x},${y}`;
  });
  return d;
}

/* ── Animated KPI Counter ──────────────────────────────── */
function AnimatedValue({ value, unit }) {
  const ref = useRef(null);
  const numericPart = parseFloat(value);
  const isNumeric = !isNaN(numericPart) && !value.includes('%');
  const hasPct = value.includes('%');

  useEffect(() => {
    if (!ref.current) return;
    if (!isNumeric && !hasPct) { ref.current.textContent = value; return; }
    const target = hasPct ? parseFloat(value) : numericPart;
    const duration = 1600;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      if (ref.current) ref.current.textContent = hasPct ? `${(eased * target).toFixed(1)}%` : current.toLocaleString();
      if (progress < 1) requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { requestAnimationFrame(tick); observer.disconnect(); }
    }, { threshold: 0.5 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, isNumeric, hasPct, numericPart]);

  return (
    <span className="dash-kpi-value">
      <span ref={ref}>0</span>
      {unit && <span className="dash-kpi-unit"> {unit}</span>}
    </span>
  );
}

/* ── Main Component ────────────────────────────────────── */
export default function DashboardPreview() {
  const chartW = 480;
  const chartH = 160;
  const areaMax = 200;

  return (
    <section className="section dash-preview-section" id="dashboard-preview">
      <div className="section-inner">
        <div className="section-label">Live Dashboard</div>
        <ScrollReveal>
          <div className="dash-title-row">
            <h2 className="section-title">
              Real data. Real decisions.<br /><strong>See what your facility reveals.</strong>
            </h2>
            <a
              href="https://site.charitysense.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary dash-explore-btn"
              onClick={trackDashboardLinkClick}
            >
              Explore Live Dashboard
              <span style={{ fontSize: 16 }}>&#8599;</span>
            </a>
          </div>
        </ScrollReveal>
        <ScrollReveal>
          <p className="section-subtitle">
            Every metric below is generated automatically from existing camera feeds &mdash;
            no manual input, no surveys, no additional hardware.
          </p>
        </ScrollReveal>

        {/* KPI Strip */}
        <ScrollReveal>
          <div className="dash-kpi-strip">
            {kpis.map((k) => (
              <div className={`dash-kpi-card${k.warn ? ' warn' : ''}`} key={k.label}>
                <div className="dash-kpi-label">{k.label}</div>
                <AnimatedValue value={k.value} unit={k.unit} />
                {k.delta && (
                  <div className={`dash-kpi-delta ${k.trend === 'up' ? 'up' : ''}`}>
                    <span className="dash-kpi-spark">↗</span> {k.delta}
                    <span className="dash-kpi-vs"> vs. last period</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Charts Grid */}
        <div className="dash-charts-grid">
          {/* Occupancy Area Chart */}
          <ScrollReveal>
            <div className="dash-chart-card">
              <div className="dash-chart-header">
                <span className="dash-chart-title">Facility Occupancy</span>
                <span className="dash-chart-stat">Avg: 74.1 &middot; P95: 162.0</span>
              </div>
              <div className="dash-chart-body">
                <svg viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="none" className="dash-area-svg">
                  <defs>
                    <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(0,229,255,0.25)" />
                      <stop offset="100%" stopColor="rgba(0,229,255,0.02)" />
                    </linearGradient>
                    <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--cyan)" />
                      <stop offset="100%" stopColor="rgba(0,229,255,0.5)" />
                    </linearGradient>
                  </defs>
                  <path d={buildAreaPath(occupancyBandHigh, areaMax, chartW, chartH, 10)} fill="rgba(0,229,255,0.08)" />
                  <path d={buildAreaPath(occupancyData, areaMax, chartW, chartH, 10)} fill="url(#bandGrad)" />
                  <path d={buildAreaPath(occupancyBandLow, areaMax, chartW, chartH, 10)} fill="var(--dark-card)" />
                  <path d={buildLinePath(occupancyData, areaMax, chartW, chartH, 10)} fill="none" stroke="url(#lineGrad)" strokeWidth="2" className="dash-line-animate" />
                </svg>
                <div className="dash-chart-xaxis">
                  {['00:00','06:00','12:00','18:00','23:00'].map(t => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
              </div>
              <div className="dash-chart-legend">
                <span className="dash-legend-item"><span className="dash-legend-dot cyan" /> Occupancy</span>
                <span className="dash-legend-item"><span className="dash-legend-dot band" /> 5-95% Band</span>
              </div>
            </div>
          </ScrollReveal>

          {/* Visit Duration Area Chart */}
          <ScrollReveal>
            <div className="dash-chart-card">
              <div className="dash-chart-header">
                <span className="dash-chart-title">Visit Duration</span>
                <span className="dash-chart-stat">Avg: 394 min &middot; P95: 1108 min</span>
              </div>
              <div className="dash-chart-body">
                <svg viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="none" className="dash-area-svg">
                  <defs>
                    <linearGradient id="visitBandGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(255,140,160,0.2)" />
                      <stop offset="100%" stopColor="rgba(255,140,160,0.02)" />
                    </linearGradient>
                    <linearGradient id="visitLineGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ff8ca0" />
                      <stop offset="100%" stopColor="rgba(255,140,160,0.5)" />
                    </linearGradient>
                  </defs>
                  <path d={buildAreaPath(visitBandHigh, 1600, chartW, chartH, 10)} fill="rgba(255,140,160,0.06)" />
                  <path d={buildAreaPath(visitData, 1600, chartW, chartH, 10)} fill="url(#visitBandGrad)" />
                  <path d={buildAreaPath(visitBandLow, 1600, chartW, chartH, 10)} fill="var(--dark-card)" />
                  <path d={buildLinePath(visitData, 1600, chartW, chartH, 10)} fill="none" stroke="url(#visitLineGrad)" strokeWidth="2" className="dash-line-animate" />
                </svg>
                <div className="dash-chart-xaxis">
                  {['00:00','06:00','12:00','18:00','23:00'].map(t => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
              </div>
              <div className="dash-chart-legend">
                <span className="dash-legend-item"><span className="dash-legend-dot visit" /> Duration</span>
                <span className="dash-legend-item"><span className="dash-legend-dot visitband" /> 5-95% Band</span>
              </div>
            </div>
          </ScrollReveal>

          {/* Occupancy Heatmap */}
          <ScrollReveal>
            <div className="dash-chart-card">
              <div className="dash-chart-header">
                <span className="dash-chart-title">Occupancy Heatmap &mdash; Day &times; Hour</span>
              </div>
              <div className="dash-heatmap-wrap">
                <div className="dash-heatmap">
                  {heatmapRows.map((row) => (
                    <div className="dash-heatmap-row" key={row.day}>
                      <span className="dash-heatmap-day">{row.day}</span>
                      {row.cells.map((val, i) => (
                        <div
                          key={i}
                          className="dash-heatmap-cell"
                          style={{ background: heatColor(val) }}
                          title={`${row.day} ${String(i).padStart(2,'0')}:00 — ${val}`}
                        />
                      ))}
                    </div>
                  ))}
                </div>
                <div className="dash-heatmap-xaxis">
                  {['00:00','06:00','12:00','18:00'].map(t => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
                <div className="dash-heatmap-scale">
                  <span>0</span>
                  <div className="dash-heatmap-gradient" />
                  <span>{heatmapMax}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Movement Patterns */}
          <ScrollReveal>
            <div className="dash-chart-card">
              <div className="dash-chart-header">
                <span className="dash-chart-title">Entrance Movement Patterns</span>
              </div>
              <div className="dash-movement-wrap">
                <svg viewBox="0 0 100 80" preserveAspectRatio="xMidYMid meet" className="dash-movement-svg">
                  {movementPaths.map((p, i) => {
                    const color = p.type === 'person' ? 'rgba(100,180,255,0.5)' : p.type === 'vehicle' ? 'rgba(100,230,180,0.45)' : 'rgba(255,140,160,0.4)';
                    return (
                      <g key={i}>
                        <path
                          d={`M${p.x1},${p.y1} Q${p.cx},${p.cy} ${p.x2},${p.y2}`}
                          fill="none"
                          stroke={color}
                          strokeWidth="0.4"
                          className="dash-trajectory"
                          style={{ animationDelay: `${i * 60}ms` }}
                        />
                        <circle cx={p.x2} cy={p.y2} r="0.8" fill={color} />
                      </g>
                    );
                  })}
                </svg>
              </div>
              <div className="dash-chart-legend">
                <span className="dash-legend-item"><span className="dash-legend-dot person" /> person</span>
                <span className="dash-legend-item"><span className="dash-legend-dot vehicle" /> vehicle</span>
                <span className="dash-legend-item"><span className="dash-legend-dot other" /> other</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
